import { Box, Button, Combobox, Stack } from "@adfenix/alexandria";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { OfficeConfig } from "types/configServiceModels";
import { selectHqOfficeModalState } from "atoms/selectedHqHoc";
import { useQuery } from "@tanstack/react-query";
import { useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import useUserInfo from "hooks/useUserInfo";
import { getOfficesByHqId } from "api/services/configService";
import { getUser } from "api/services/authService";
import { ConfigurationService } from "@adfenix/types";

interface ModalSelectHqOfficeProps {
  hqs?: ConfigurationService.HqAndDomainModel[];
  preSelectedHqId?: string;
  shouldSelectOffice: boolean;
  onlyShowAuthorizedOffices: boolean;
  showLabels?: boolean;
  onSelectOffice?: (officeId: string) => void;
  onSelectHq?: (hqId: string) => void;
  hideSubmit?: boolean;
}

export const SelectHqOfficeCombobox = ({
  hqs,
  preSelectedHqId,
  shouldSelectOffice,
  onlyShowAuthorizedOffices,
  showLabels,
  onSelectHq,
  onSelectOffice,
  hideSubmit
}: ModalSelectHqOfficeProps) => {
  const { userEmail, accessLevel } = useUserInfo();
  const { t } = useTranslation();
  const [selectedOffice, setSelectedOffice] = useState<OfficeConfig>();

  const navigate = useNavigate();
  const location = useLocation();

  const { data: userInfo } = useQuery(
    ["getUser", userEmail],
    () => getUser(userEmail),
    { enabled: Boolean(userEmail && onlyShowAuthorizedOffices) }
  );

  const [selectedHq, setSelectedHq] = useState<
    ConfigurationService.HqAndDomainModel | undefined
  >(hqs?.find(hq => hq.id === preSelectedHqId));

  const sortedHqs = hqs
    ?.filter(Boolean)
    .slice(0)
    .sort((a, b) => (a.name < b.name ? -1 : 1));

  const { data: offices } = useQuery(
    ["getOffices", selectedHq?.id],
    () => getOfficesByHqId(selectedHq?.id ?? ""),
    {
      enabled: Boolean(selectedHq?.id) && shouldSelectOffice
    }
  );

  const setHqOfficeModalState = useSetRecoilState(selectHqOfficeModalState);

  const handleSwitchSelection = () => {
    const root = location.pathname.startsWith("/customer")
      ? "customer"
      : "back-office";

    const currentRoute = location.pathname
      .split("/")
      .slice(shouldSelectOffice ? 6 : 4)
      .join("/");

    shouldSelectOffice
      ? navigate(
          `/${root}/hq/${selectedHq?.id}/office/${selectedOffice?.id}/${currentRoute}`
        )
      : navigate(`/${root}/hq/${selectedHq?.id}/${currentRoute}`);
  };

  const handleApply = () => {
    handleSwitchSelection();
    setHqOfficeModalState(false);
  };

  return (
    <Stack spacing="gutter">
      <Combobox
        label={showLabels ? "Hq" : undefined}
        items={sortedHqs ?? []}
        valueObjectIndexAccessor="name"
        isSearchable
        onChange={value => {
          setSelectedHq(hqs?.find(hq => hq.domain === value.domain) ?? value);

          if (onSelectHq) onSelectHq(value.id);
        }}
        defaultItem={selectedHq}
        placeholder={t("listings.select.agency.modal.placeholder")}
        classNamePrefix="backoffice-hq-office-combobox"
      />

      {shouldSelectOffice && (
        <Combobox
          label={showLabels ? "Office" : undefined}
          items={
            accessLevel == "Office" && onlyShowAuthorizedOffices
              ? offices?.filter(x =>
                  userInfo?.offices.some(d => d.id == x.id)
                ) ?? []
              : offices ?? []
          }
          valueObjectIndexAccessor="name"
          isSearchable
          disabled={!selectedHq}
          onChange={value => {
            setSelectedOffice(value);

            if (onSelectOffice) onSelectOffice(value.id);
          }}
          placeholder={"Choose office"}
        />
      )}

      {!hideSubmit && (
        <Box pY="xl2" display="flex" justifyContent="flex-end">
          <Button
            logLabel="hq-selector-submit-button"
            style={{ flexGrow: 1 }}
            color="primary"
            onClick={() => handleApply()}
            disabled={shouldSelectOffice ? !selectedOffice : !selectedHq}
            data-testid="hq-selector-submit"
          >
            Select
          </Button>
        </Box>
      )}
    </Stack>
  );
};

export default SelectHqOfficeCombobox;
